import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Wallet, Gift } from "lucide-react";
import api from "@/lib/api";

export default function Signup() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref");
  const inviteCode = params.get("invite");
  const prefillEmail = params.get("email") || "";
  const { setUser } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name, email, password };
      if (refCode) body.referral_code = refCode;
      if (inviteCode) body.invite_code = inviteCode;
      const { data } = await api.post("/auth/signup", body);
      localStorage.setItem("mw_token", data.token);
      setUser(data.user);
      if (data.granted_premium_days > 0) {
        const months = Math.round(data.granted_premium_days / 30);
        toast.success(`${months} month${months > 1 ? 's' : ''} of Premium unlocked!`);
      } else {
        toast.success("Account created");
      }
      nav("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="max-w-md mx-auto w-full flex flex-col flex-1 px-6 py-10">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-10 h-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
            <Wallet size={20} strokeWidth={1.75} />
          </div>
          <div className="font-manrope font-bold text-lg tracking-tight text-slate-900">MoneyWise</div>
        </div>

        {inviteCode && (
          <div data-testid="invite-banner" className="mb-6 rounded-2xl bg-slate-900 text-white p-4 flex items-center gap-3">
            <Gift className="text-emerald-400 shrink-0" size={22} />
            <div>
              <div className="font-manrope font-bold text-sm">You're invited</div>
              <div className="text-xs text-slate-300 mt-0.5">Complete signup to unlock 6 months of Premium — free.</div>
            </div>
          </div>
        )}
        {refCode && !inviteCode && (
          <div data-testid="ref-banner" className="mb-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
            <Gift className="text-emerald-600 shrink-0" size={20} />
            <div>
              <div className="font-manrope font-bold text-slate-900 text-sm">Referred by a friend</div>
              <div className="text-xs text-emerald-700 mt-0.5">You'll get 1 month of Premium when you sign up.</div>
            </div>
          </div>
        )}

        <h1 className="font-manrope text-4xl font-extrabold tracking-tighter text-slate-900">Start finding money.</h1>
        <p className="text-slate-500 mt-3">Takes under a minute. Your data stays private.</p>
        <form onSubmit={submit} className="mt-10 space-y-4">
          <input data-testid="signup-name-input" placeholder="Your name" required value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-colors duration-200" />
          <input data-testid="signup-email-input" type="email" placeholder="Email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-colors duration-200" />
          <input data-testid="signup-password-input" type="password" placeholder="Password (6+ chars)" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-colors duration-200" />
          <button data-testid="signup-submit-btn" disabled={busy} className="w-full bg-slate-900 text-white rounded-full py-4 font-semibold hover:bg-slate-800 active:scale-[0.98] transition-transform duration-150 disabled:opacity-60">{busy ? "Creating…" : "Create account"}</button>
        </form>
        <div className="mt-8 text-center text-sm text-slate-500">
          Have an account? <Link data-testid="login-link" to="/login" className="text-slate-900 font-semibold">Log in</Link>
        </div>
      </div>
    </div>
  );
}
